import React, { useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import {
  Button,
  FormControl,
  Message,
  Paragraph,
  TextFieldAdapter,
  validations,
} from '../../../../shared/components';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { addBlock, fetchBlocks, patchBlock } from 'src/modules/shared/slices/blocks/api/blocks';
import { ValidationFunctions } from 'src/modules/shared/components/forms/validations';

type AddContentDialogProps = {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<boolean | undefined> | Function;
  bookId: string;
  pageId: string;
  edit?: boolean | undefined;
  blockId?: string | undefined;
  sectionId?: string;
  content?: string | undefined;
  type?: string | undefined;
  previousBlockId?: string;
  clusterId?: string;
  validationType: keyof ValidationFunctions;
};

export const AddContentDialog: React.FC<AddContentDialogProps> = ({
  dialogOpen,
  setDialogOpen,
  bookId,
  pageId,
  edit = false,
  blockId = '',
  sectionId,
  content,
  type = 'video',
  validationType = 'isVideo',
  previousBlockId,
  clusterId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const error = useAppSelector((state) => state.blocks.error);

  const addContent = useCallback(
    (url: string) => {
      if (edit) {
        dispatch(
          patchBlock({
            bookId,
            pageId,
            blockId,
            payload: {
              type,
              title: t(`trajectory.page.blocks.${type}.title`),
              content: url,
              previous_block_id: previousBlockId,
            },
          })
        );
        dispatch(fetchBlocks(pageId));
      } else {
        dispatch(
          addBlock({
            bookId,
            pageId,
            payload: {
              type,
              title: t(`trajectory.page.blocks.${type}.title`),
              content: url,
              previous_block_id: sectionId === previousBlockId ? '' : previousBlockId,
              cluster_id: clusterId,
              section_id: sectionId,
            },
          })
        );
      }
    },
    [dispatch, bookId, pageId, blockId, edit, t, type, previousBlockId, clusterId, sectionId]
  );

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      aria-labelledby={`add-${type}-form`}
    >
      <Form
        onSubmit={(values: any) => {
          addContent(values.url);
          setDialogOpen(undefined);
        }}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>{t(`trajectory.page.blocks.${type}.form.title`)}</DialogTitle>
            <DialogContent>
              <Paragraph>{t(`trajectory.page.blocks.${type}.form.intro`)}</Paragraph>
              <FormControl label={t(`trajectory.page.blocks.${type}.form.url`)} id="url" required>
                <Field
                  name="url"
                  component={TextFieldAdapter}
                  validate={validations(t)[validationType] as any}
                  defaultValue={content}
                />
              </FormControl>
              {error && <Message type="error">{t(`shared.errors.generic`)}</Message>}
            </DialogContent>
            <DialogActions>
              <Button variant="text" onClick={() => setDialogOpen(false)}>
                {t('shared.forms.cancel')}
              </Button>
              <Button type="submit">{t('shared.forms.confirm')}</Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};
